import {
  DrawerFooterArea,
  Drawer as DrawerType,
  ControlcenterMelcoModelsLicenseCreateResponse,
} from "melco-shared-logic";
import {
  Col,
  Drawer,
  FullWidthSpace,
  Row,
  Steps,
  DesignTokens,
} from "melco-ui";
import { useState } from "react";
import { CopyLicense } from "../form/CopyLicense";
import {
  UpgradeLicenseForm,
  UpgradeLicenseFormValueType,
} from "../form/upgrade/UpgradeLicenseForm";
import { UpgradeLicenseSummary } from "../form/upgrade/UpgradeLicenseSummary";
import { UpgradeLicenseVersionAndLevel } from "../form/upgrade/UpgradeLicenseVersionAndLevel";
import { useTranslation } from "react-i18next";

const { spacings } = DesignTokens;

type UpgradeLicenseDrawerProps = {
  drawer: DrawerType;
};

export type NewSelectedValuesForLicenseUpgrade = {
  new_version_id: string;
  new_subscription_code: string;
  new_version_id_display_value: string;
  new_subscription_code_display_value: string;
};

export const UpgradeLicenseDrawer: React.FC<UpgradeLicenseDrawerProps> = ({
  drawer,
}) => {
  const { closeDrawer, isDrawerOpen } = drawer;
  const [currentStep, setCurrentStep] = useState(0);
  const [licenseFormValues, setLicenseFormValues] =
    useState<UpgradeLicenseFormValueType>();
  const [upgradeLicenseResponse, setUpgradeLicenseResponse] = useState<
    ControlcenterMelcoModelsLicenseCreateResponse | undefined
  >();
  const [currentLicense, setCurrentLicense] = useState<
    ControlcenterMelcoModelsLicenseCreateResponse | undefined
  >();
  const [newValuesToUpgrade, setNewValuesToUpgrade] =
    useState<NewSelectedValuesForLicenseUpgrade>();

  const { t } = useTranslation();

  const onCloseDrawer = () => {
    setCurrentStep(0);
    closeDrawer();
  };

  const onUpgradeLicenseStep1CallBackFn = (
    values: UpgradeLicenseFormValueType,
    currentLicense: ControlcenterMelcoModelsLicenseCreateResponse | undefined
  ) => {
    setLicenseFormValues(values);
    setCurrentStep(currentStep + 1);
    setCurrentLicense(currentLicense);
  };

  const onUpgradeLicenseStep2CallBackFn = (
    newValuesToUpgrade: NewSelectedValuesForLicenseUpgrade
  ) => {
    setCurrentStep(currentStep + 1);

    setNewValuesToUpgrade(newValuesToUpgrade);
  };

  const onUpgradeLicenseStep3CallBackFn = (
    obj: ControlcenterMelcoModelsLicenseCreateResponse | undefined
  ) => {
    setUpgradeLicenseResponse(obj);
    setCurrentStep(currentStep + 1);
  };

  const stepback = () => {
    setCurrentStep(currentStep - 1);
  };

  const forms: any = [
    <UpgradeLicenseForm
      onUpgradeLicenseCallBackFn={onUpgradeLicenseStep1CallBackFn}
    />,

    <UpgradeLicenseVersionAndLevel
      stepback={stepback}
      callbackFn={onUpgradeLicenseStep2CallBackFn}
      currentLicense={currentLicense}
      licenseFormValues={licenseFormValues}
    />,

    <UpgradeLicenseSummary
      stepback={stepback}
      callbackFn={onUpgradeLicenseStep3CallBackFn}
      currentLicense={currentLicense}
      newValuesToUpgrade={newValuesToUpgrade}
      licenseFormValues={licenseFormValues}
    />,

    <CopyLicense
      successMessage={t("licensemanager.upgrade.copy.message").replace(
        "[email]",
        currentLicense?.email_address ?? ""
      )}
      createLicenseResponse={upgradeLicenseResponse}
      onCloseDrawer={onCloseDrawer}
      licenseFormvalues={licenseFormValues}
      title={t("licensemanager.upgrade.copy.title")}
    />,
  ];

  // isDrawerOPen is used multiple times to display UI , basically to refresh DOM and build components in DOM again
  // As Footer section was becoming empty if go from one Drawer to other like Create Drawer, Upgrade Drawer, Change Level Drawer
  return (
    <>
      {isDrawerOpen && (
        <Drawer
          title={t("licensemanager.cards.upgrade.title")}
          placement="right"
          width={1000}
          closable={true}
          onClose={onCloseDrawer}
          open={isDrawerOpen}
          footer={isDrawerOpen && <DrawerFooterArea />}
        >
          <FullWidthSpace direction="vertical">
            <Steps
              items={[
                {
                  title: t("licensemanager.upgrade.steps.checklicense"),
                },

                { title: t("licensemanager.upgrade.steps.version&level") },

                { title: t("licensemanager.upgrade.steps.summary") },
                {
                  title: t("licensemanager.upgrade.steps.copylicense"),
                },
              ]}
              current={currentStep}
              labelPlacement="vertical"
              size="small"
            />

            <FullWidthSpace direction="vertical" size="middle">
              {/* <Title level={4}>Define customer & currentLicense details</Title> */}

              <div>
                <Row gutter={[spacings.middle, 0]}>
                  <Col xs={24} md={24}>
                    {isDrawerOpen && forms[currentStep]}
                  </Col>
                </Row>
              </div>
            </FullWidthSpace>
          </FullWidthSpace>
        </Drawer>
      )}
    </>
  );
};
